import React, { useMemo } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/ui/page-header';
import { SegmentedTabs } from 'components/ui/data-table';
import { StatusTabs } from 'components/ui/status-tabs';
import { Button } from 'components/ui/button';
import { RequireFeature } from 'components/entitlements/RequireFeature';
import { useFeature, FEATURES } from 'modules/entitlements';
import { useOverview, useIntegrity, useTests } from 'modules/analytics';
import { usePendingGrading } from 'modules/grading';
import { useAuthContext } from 'modules/auth';
import config from 'config';
import { useIsMobile } from 'hooks/use-media-query';
import { DashboardMobile } from 'components/mobile/screens/DashboardMobile';
import { useAnalyticsFilters, type AnalyticsTab, type DatePreset } from './useAnalyticsFilters';
import { PERIOD_LABEL_KEY, countPendingAnswers, groupPendingByTest } from './analyticsFormat';
import KpiRow from './components/KpiRow';
import AnalyticsFilterBar from './components/AnalyticsFilterBar';
import OverviewTab from './components/OverviewTab';
import PeopleTab from './components/PeopleTab';
import TestsQuestionsTab from './components/TestsQuestionsTab';
import IntegrityTab from './components/IntegrityTab';
import { DemoWorkspaceBanner, useProductTour } from 'modules/tour';

const AnalyticsDesktop: React.FC = () => {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  // useOrgStore is not populated in the customer app, so reading it here left
  // orgId undefined and every analytics query stayed disabled (empty page).
  const orgId =
    localStorage.getItem(config.api.active_org_key) ||
    authState.organization?.id ||
    undefined;
  const filters = useAnalyticsFilters();

  // The advanced surfaces (/tests, /integrity, /people) are PRO-gated on the
  // server; only fetch them when entitled so non-PRO orgs don't 403.
  const hasAdvanced = useFeature(FEATURES.ADVANCED_ANALYTICS);
  const overview = useOverview({ orgId, params: filters.params });
  const integrity = useIntegrity({ orgId, params: filters.params, enabled: hasAdvanced });
  const tests = useTests({ orgId, params: filters.params, enabled: hasAdvanced });
  const grading = usePendingGrading(orgId);

  const pendingGroups = useMemo(() => groupPendingByTest(grading.data), [grading.data]);
  const pendingAnswers = countPendingAnswers(pendingGroups);
  const integrityEvents = integrity.data
    ? integrity.data.byType.reduce((sum, b) => sum + b.count, 0)
    : 0;
  const reviewIntegrity = () => filters.setTab('integrity');

  // Client-side CSV of the overview KPIs — a real, if lightweight, export.
  const handleExport = () => {
    const d = overview.data;
    if (!d) return;
    const rows: [string, string | number][] = [
      [t('kpi_active_tests'), d.activeTests],
      [t('kpi_respondents'), d.totalCandidates],
      [t('kpi_submissions'), d.totalAttempts],
      [t('completed'), d.completedAttempts],
      [t('kpi_avg_score'), `${d.avgScore}%`],
      [t('pass_rate'), `${d.passRate}%`],
      [t('kpi_pending_grading'), pendingAnswers],
      [t('kpi_integrity_events'), integrityEvents],
    ];
    const csv = [['Metric', 'Value'], ...rows].map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${filters.preset}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs: Array<{ value: AnalyticsTab; label: string }> = [
    { value: 'overview', label: t('analytics_tab_overview') },
    { value: 'people', label: t('analytics_tab_people') },
    { value: 'tests', label: t('analytics_tab_tests') },
    { value: 'integrity', label: t('analytics_tab_integrity') },
  ];
  const activeTab = tabs.find((tab) => tab.value === filters.tab) ?? tabs[0];
  const { startTour } = useProductTour();

  return (
    <div data-analytics-desktop id="dashboard-overview-card" className="flex h-full min-h-0 flex-col gap-4">
      {/* Demo Workspace Starter Pack Banner */}
      <DemoWorkspaceBanner onStartTour={startTour} />

      {/* Header — title, the period and Export */}
      <PageHeader
        title={t('nav.analytics')}
        actions={
          <>
            <SegmentedTabs<DatePreset>
              aria-label={t('analytics_period')}
              options={[
                { label: t('analytics_range_today'), value: 'today' },
                { label: t('analytics_range_7d'), value: '7d' },
                { label: t('analytics_range_30d'), value: '30d' },
                { label: t('analytics_range_term'), value: 'term' },
              ]}
              value={filters.preset}
              onChange={filters.setPreset}
            />
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExport} disabled={!overview.data}>
              <Download className="h-4 w-4" aria-hidden />
              {t('export')}
            </Button>
          </>
        }
      />

      {/* KPI strip — windowed by the active filter, visible across tabs */}
      <KpiRow
        data={overview.data}
        isLoading={overview.isLoading}
        pendingAnswers={pendingAnswers}
        integrityEvents={hasAdvanced && integrity.data ? integrityEvents : null}
        funnel={tests.data?.funnel}
        onReviewIntegrity={reviewIntegrity}
      />

      {/* Tab line — the test filter sits at its right end, on the same rule */}
      <div className="flex items-end">
        <StatusTabs<AnalyticsTab>
          aria-label={t('nav.analytics')}
          className="min-w-0 flex-1"
          options={tabs}
          value={activeTab.value}
          onChange={filters.setTab}
        />
        <div className="flex shrink-0 items-center border-b border-border pb-1.5 pl-4">
          <AnalyticsFilterBar filters={filters} orgId={orgId} showTeamFilter={activeTab.value === 'people'} />
        </div>
      </div>

      {/* The scrollbar sits in the page gutter (negative margin + a stable
          gutter), so the panels line up with the KPI row and the tab line. */}
      <div
        role="tabpanel"
        aria-label={activeTab.label}
        data-analytics-scroll-region
        className="-mr-3 min-h-0 flex-1 overflow-y-auto pr-1 scrollbar-thin [scrollbar-gutter:stable]"
      >
        {activeTab.value === 'overview' && (
          <OverviewTab
            data={overview.data}
            isLoading={overview.isLoading}
            isError={overview.isError}
            errorMessage={overview.error instanceof Error ? overview.error.message : undefined}
            pendingGroups={pendingGroups}
            pendingLoading={grading.isLoading}
            integrityEvents={integrityEvents}
            hasAdvanced={hasAdvanced}
            testsData={tests.data}
            testsLoading={tests.isLoading}
            periodLabel={t(PERIOD_LABEL_KEY[filters.preset])}
            onReviewIntegrity={reviewIntegrity}
            onShowAllTests={() => filters.setTab('tests')}
          />
        )}
        {activeTab.value === 'people' && (
          <RequireFeature feature={FEATURES.ADVANCED_ANALYTICS} requiredPlan="PRO">
            <PeopleTab orgId={orgId} filters={filters} />
          </RequireFeature>
        )}
        {activeTab.value === 'tests' && (
          <RequireFeature feature={FEATURES.ADVANCED_ANALYTICS} requiredPlan="PRO">
            <TestsQuestionsTab orgId={orgId} filters={filters} />
          </RequireFeature>
        )}
        {activeTab.value === 'integrity' && (
          <RequireFeature feature={FEATURES.ADVANCED_ANALYTICS} requiredPlan="PRO">
            <IntegrityTab orgId={orgId} filters={filters} />
          </RequireFeature>
        )}
      </div>
    </div>
  );
};

// Phones render the dedicated mobile dashboard; the switch is a thin wrapper so
// hook count stays constant across the breakpoint.
const Analytics: React.FC = () => (useIsMobile() ? <DashboardMobile /> : <AnalyticsDesktop />);

export default Analytics;
